"use client";

import { useEffect, useId, useLayoutEffect, useRef, useState } from "react";
import { stegaClean } from "@sanity/client/stega";
import DragCursor from "@/components/global/ui/DragCursor";
import type { PageAiFeaturesSchema } from "@/sanity/pages/PageAiFeaturesTemplate";
import { SectionHeading } from "./ui";
import WrappedStory, { wrappedStoryDescription } from "./WrappedStory";
import pageStyles from "./ai-features.module.scss";
import styles from "./wrapped-stories.module.scss";

const STORY_DURATION = 4000;

export default function WrappedSection({
  data,
}: {
  data?: PageAiFeaturesSchema["wrapped"];
}) {
  const descriptionId = useId();
  const trackRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef<HTMLSpanElement>(null);
  const timer = useRef<Animation | null>(null);
  const frame = useRef<number | null>(null);
  const cursor = useRef(3);
  const renderedAnchor = useRef(3);
  const target = useRef(3);
  const pendingElapsed = useRef(0);
  const moving = useRef(false);
  const advance = useRef(() => {});
  const swipe = useRef<{
    id: number;
    x: number;
    y: number;
    cursor: number;
    pitch: number;
    selected: number;
    pressed: number | null;
    resumeTarget: number;
    elapsed: number;
    dragging: boolean;
  } | null>(null);
  const suppressClick = useRef(false);
  const focusSelection = useRef(false);
  const [position, setPosition] = useState(3);
  const [live, setLive] = useState(3);
  const [anchor, setAnchor] = useState(3);
  const [cycle, setCycle] = useState(0);
  const [inView, setInView] = useState(false);
  const [artworkLoaded, setArtworkLoaded] = useState(false);
  const [visible, setVisible] = useState(true);
  const [reduced, setReduced] = useState(true);
  const [focused, setFocused] = useState(false);
  const [pressing, setPressing] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [settling, setSettling] = useState(false);
  const cards = data?.cards ?? [];
  const hasCards = cards.length > 0;
  const selected = cards.length
    ? ((position % cards.length) + cards.length) % cards.length
    : 0;
  const awake = inView && visible;
  const awakeRef = useRef(awake);
  awakeRef.current = awake;
  const playing = awake && !reduced && !focused && !pressing && !settling;
  const playingRef = useRef(playing);
  playingRef.current = playing;

  function paint(value: number) {
    cursor.current = value;
    trackRef.current?.style.setProperty(
      "--wrapped-shift",
      String(value - renderedAnchor.current),
    );
    const nextAnchor = Math.round(value);
    if (nextAnchor !== renderedAnchor.current) setAnchor(nextAnchor);
  }

  function stopMotion() {
    if (frame.current !== null) cancelAnimationFrame(frame.current);
    frame.current = null;
  }

  function settleTo(next: number, elapsed = 0) {
    stopMotion();
    target.current = next;
    setLive(next);
    pendingElapsed.current = elapsed;
    timer.current?.pause();
    const from = cursor.current;
    const duration = reduced
      ? 0
      : Math.min(600, Math.max(180, Math.abs(next - from) * 600));
    const complete = () => {
      paint(next);
      frame.current = null;
      moving.current = false;
      setSettling(false);
      setPosition(next);
      setCycle((value) => value + 1);
    };
    if (Math.abs(next - from) < 0.0001 || !duration) {
      complete();
      return;
    }
    moving.current = true;
    setSettling(true);
    if (!awakeRef.current || swipe.current) return;
    const started = performance.now();
    const tick = (now: number) => {
      const progress = Math.max(0, Math.min(1, (now - started) / duration));
      paint(from + (next - from) * (1 - (1 - progress) ** 5));
      if (progress < 1) frame.current = requestAnimationFrame(tick);
      else complete();
    };
    frame.current = requestAnimationFrame(tick);
  }

  function move(offset: number) {
    if (cards.length < 2 || !offset) return;
    settleTo((moving.current ? target.current : position) + offset);
  }
  advance.current = () => {
    if (!swipe.current && !moving.current && playingRef.current)
      settleTo(position + 1);
  };

  function finishGesture(pointerId: number, cancelled = false) {
    const gesture = swipe.current;
    if (!gesture || gesture.id !== pointerId) return;
    const track = trackRef.current;
    swipe.current = null;
    setPressing(false);
    setDragging(false);
    if (track?.hasPointerCapture(pointerId))
      track.releasePointerCapture(pointerId);
    const next =
      gesture.dragging && !cancelled
        ? Math.round(cursor.current)
        : !cancelled &&
            gesture.pressed !== null &&
            gesture.pressed !== gesture.selected
          ? gesture.pressed
          : gesture.resumeTarget;
    suppressClick.current = true;
    if (
      gesture.dragging ||
      next !== gesture.selected ||
      Math.abs(cursor.current - next) > 0.0001
    )
      settleTo(next, next === gesture.selected ? gesture.elapsed : 0);
  }

  useLayoutEffect(() => {
    renderedAnchor.current = anchor;
    trackRef.current?.style.setProperty(
      "--wrapped-shift",
      String(cursor.current - anchor),
    );
  }, [anchor]);

  useLayoutEffect(() => {
    if (!focusSelection.current) return;
    focusSelection.current = false;
    const track = trackRef.current;
    if (track?.contains(document.activeElement))
      track
        .querySelector<HTMLButtonElement>("button[data-active='true']")
        ?.focus({ preventScroll: true });
  }, [position, cycle]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const motion = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncMotion = () => setReduced(motion.matches);
    const syncVisibility = () => setVisible(!document.hidden);
    syncMotion();
    syncVisibility();
    motion.addEventListener("change", syncMotion);
    document.addEventListener("visibilitychange", syncVisibility);
    const observer = new IntersectionObserver(
      ([entry]) => setInView(entry.isIntersecting),
      { threshold: 0.35 },
    );
    observer.observe(track);
    const artworkObserver = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return;
        setArtworkLoaded(true);
        artworkObserver.disconnect();
      },
      { rootMargin: "800px" },
    );
    artworkObserver.observe(track);
    return () => {
      observer.disconnect();
      artworkObserver.disconnect();
      motion.removeEventListener("change", syncMotion);
      document.removeEventListener("visibilitychange", syncVisibility);
    };
  }, [hasCards]);

  useEffect(() => {
    const values = [
      ...(trackRef.current?.querySelectorAll<HTMLElement>("[data-story-value]") ?? []),
    ]
      .filter((element) => {
        const card = element.closest<HTMLElement>("[data-slot]");
        if (card?.dataset.live === "true") return true;
        element.textContent = element.dataset.storyValue || "";
        return false;
      })
      .map((element) => {
        const final = element.dataset.storyValue || "";
        const raw = final.match(/\d+(?:[.,]\d+)*/)?.[0];
        if (!raw) return { element, final, format: () => final };
        const fraction = raw.match(/[.,](\d{1,2})$/)?.[1] || "";
        const decimal = fraction ? raw[raw.length - fraction.length - 1] : "";
        const group =
          [...raw].find((char) => /[.,]/.test(char) && char !== decimal) || "";
        const normalized = group ? raw.replaceAll(group, "") : raw;
        const value = Number(
          decimal ? normalized.replace(decimal, ".") : normalized,
        );
        return {
          element,
          final,
          format: (progress: number) => {
            if (progress >= 1) return final;
            const [whole, part] = (value * (1 - (1 - progress) ** 3))
              .toFixed(fraction.length)
              .split(".");
            const formatted = group
              ? whole.replace(/\B(?=(\d{3})+(?!\d))/g, group)
              : whole;
            return final.replace(raw, formatted + (part ? decimal + part : ""));
          },
        };
      });
    if (reduced) {
      values.forEach(({ element, final }) => {
        element.textContent = final;
      });
      return;
    }
    if (!awake || pressing || focused) return;
    let frame = 0;
    let wake = 0;
    const sample = () => {
      let counting = false;
      let next = 0;
      values.forEach(({ element, format }) => {
        const animation = element.getAnimations()[0];
        if (!animation) return;
        const time = Number(animation.currentTime || 0);
        const progress = Math.max(0, Math.min(1, (time - 250) / 1300));
        const text = format(progress);
        if (element.textContent !== text) element.textContent = text;
        if (time >= 250 && progress < 1) counting = true;
        else if (time < 250) next = 250 - time;
      });
      if (counting) frame = requestAnimationFrame(sample);
      else if (next) wake = window.setTimeout(sample, next);
    };
    sample();
    return () => {
      cancelAnimationFrame(frame);
      window.clearTimeout(wake);
    };
  }, [anchor, live, awake, reduced, pressing, focused, cards]);

  useEffect(() => {
    const progress = progressRef.current;
    if (!progress || reduced || cards.length < 2) return;
    const animation = progress.animate(
      [{ transform: "scaleX(0)" }, { transform: "scaleX(1)" }],
      { duration: STORY_DURATION, fill: "both", easing: "linear" },
    );
    animation.pause();
    animation.currentTime = Math.min(STORY_DURATION, pendingElapsed.current);
    pendingElapsed.current = 0;
    animation.onfinish = () => advance.current();
    timer.current = animation;
    return () => {
      animation.cancel();
      timer.current = null;
    };
  }, [position, cycle, reduced, cards.length]);

  useEffect(() => {
    if (!playing) timer.current?.pause();
    else if (Number(timer.current?.currentTime) >= STORY_DURATION)
      advance.current();
    else timer.current?.play();
  }, [playing, position, cycle, reduced]);

  useEffect(() => {
    if (!awake) {
      stopMotion();
      if (swipe.current) finishGesture(swipe.current.id, true);
    } else if (moving.current && frame.current === null && !swipe.current)
      settleTo(target.current, pendingElapsed.current);
    // The pending target is intentionally read from refs when visibility changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awake]);

  useEffect(
    () => () => {
      if (frame.current !== null) cancelAnimationFrame(frame.current);
    },
    [],
  );

  if (!data || !cards.length) return null;
  // A guard on each side lets long drags recycle cards beyond the visible ring.
  const radius = Math.min(3, Math.ceil((cards.length - 1) / 2)) + 1;
  const positions = Array.from(
    { length: radius * 2 + 1 },
    (_, i) => anchor + i - radius,
  );
  const exposedPositions = new Map<number, number>();
  for (const absolute of positions) {
    if (Math.abs(absolute - anchor) >= radius) continue;
    const index = ((absolute % cards.length) + cards.length) % cards.length;
    const previous = exposedPositions.get(index);
    if (
      previous === undefined ||
      Math.abs(absolute - anchor) < Math.abs(previous - anchor)
    )
      exposedPositions.set(index, absolute);
  }
  return (
    <section
      id="wrapped"
      className={pageStyles.wrapped}
      aria-label="Session wrapped stories"
    >
      <div className="px-[3rem]">
        <SectionHeading data={data} />
      </div>
      <div
        ref={trackRef}
        className={styles.track}
        tabIndex={0}
        role="region"
        aria-label="Session stories. Use left and right arrows to browse. Focus a story to pause."
        aria-roledescription="carousel"
        data-artwork-loaded={artworkLoaded}
        data-playing={playing}
        data-awake={awake}
        data-reduced={reduced}
        data-pressing={pressing}
        data-dragging={dragging}
        data-drag-cursor={cards.length > 1}
        data-settling={settling}
        data-focused={focused}
        onFocus={(event) =>
          setFocused(!swipe.current && event.target.matches(":focus-visible"))
        }
        onBlur={(event) => {
          if (!event.currentTarget.contains(event.relatedTarget))
            setFocused(false);
        }}
        onDragStart={(event) => event.preventDefault()}
        onPointerDown={(event) => {
          if (!event.isPrimary || event.button !== 0 || cards.length < 2)
            return;
          timer.current?.pause();
          const resumeTarget = moving.current ? target.current : position;
          stopMotion();
          moving.current = false;
          setSettling(false);
          setPressing(true);
          setFocused(false);
          if (event.pointerType === "mouse") {
            event.preventDefault();
            const activeElement = document.activeElement;
            if (
              activeElement instanceof HTMLElement &&
              event.currentTarget.contains(activeElement)
            )
              activeElement.blur();
            event.currentTarget.setPointerCapture(event.pointerId);
          }
          suppressClick.current = false;
          const pressedCard = (event.target as Element).closest<HTMLElement>(
            "button[data-slot]",
          );
          swipe.current = {
            id: event.pointerId,
            x: event.clientX,
            y: event.clientY,
            cursor: cursor.current,
            pitch: Number.parseFloat(
              getComputedStyle(event.currentTarget).getPropertyValue(
                "--wrapped-step-one",
              ),
            ),
            selected: position,
            pressed: pressedCard
              ? renderedAnchor.current + Number(pressedCard.dataset.slot)
              : null,
            resumeTarget,
            elapsed: Number(timer.current?.currentTime ?? 0),
            dragging: false,
          };
        }}
        onPointerMove={(event) => {
          const gesture = swipe.current;
          if (!gesture || gesture.id !== event.pointerId) return;
          const x = event.clientX - gesture.x,
            y = event.clientY - gesture.y;
          if (!gesture.dragging) {
            if (Math.max(Math.abs(x), Math.abs(y)) < 8) return;
            if (Math.abs(y) >= Math.abs(x)) {
              if (event.pointerType !== "mouse")
                finishGesture(event.pointerId, true);
              return;
            }
            gesture.dragging = true;
            event.currentTarget.setPointerCapture(event.pointerId);
            setDragging(true);
          }
          paint(gesture.cursor - x / gesture.pitch);
        }}
        onClickCapture={(event) => {
          if (!suppressClick.current) return;
          event.preventDefault();
          event.stopPropagation();
          suppressClick.current = false;
        }}
        onPointerCancel={(event) => finishGesture(event.pointerId, true)}
        onLostPointerCapture={(event) => {
          if (event.target === event.currentTarget)
            finishGesture(event.pointerId, true);
        }}
        onPointerUp={(event) => finishGesture(event.pointerId)}
        onKeyDown={(event) => {
          suppressClick.current = false;
          setFocused(true);
          if (event.key === "ArrowRight" || event.key === "ArrowLeft") {
            event.preventDefault();
            focusSelection.current = true;
            move(event.key === "ArrowRight" ? 1 : -1);
          }
        }}
      >
        {positions.map((absolute) => {
          const index =
            ((absolute % cards.length) + cards.length) % cards.length;
          const card = cards[index],
            slot = absolute - anchor,
            active = absolute === position;
          const exposed = exposedPositions.get(index) === absolute;
          const description = wrappedStoryDescription(card);
          const html =
            !!card.storyType && stegaClean(card.storyType) !== "image";
          return (
            <button
              key={absolute}
              type="button"
              className={styles.card}
              data-slot={slot}
              data-distance={Math.abs(slot)}
              data-active={active}
              data-live={absolute === live}
              aria-label={`${card.alt || card.storyTitle || "Session story"}, ${index + 1} of ${cards.length}`}
              aria-pressed={active}
              aria-hidden={!exposed || undefined}
              aria-describedby={
                exposed && description
                  ? `${descriptionId}-${absolute}`
                  : undefined
              }
              tabIndex={active && exposed ? 0 : -1}
              onClick={() => {
                if (!active) settleTo(absolute);
              }}
            >
              <WrappedStory card={card} />
              {exposed && description && (
                <span
                  id={`${descriptionId}-${absolute}`}
                  className={styles.accessibleDescription}
                >
                  {description}
                </span>
              )}
              {html && (
                <span className={styles.segments} aria-hidden="true">
                  {cards.map((item, i) => (
                    <span key={item._key} data-complete={i < index}>
                      <span
                        ref={active && i === index ? progressRef : undefined}
                        data-current={i === index}
                      />
                    </span>
                  ))}
                </span>
              )}
              {!html && (
                <span className={styles.imageProgress} aria-hidden="true">
                  <span ref={active ? progressRef : undefined} />
                </span>
              )}
            </button>
          );
        })}
      </div>
      {cards.length > 1 && <DragCursor targetRef={trackRef} />}
      <div className={styles.controls} aria-hidden="true">
        {cards.map((card, index) => (
          <span
            key={card._key}
            className={styles.pagerDot}
            data-active={index === selected}
          />
        ))}
      </div>
    </section>
  );
}
